import { createRouter, createWebHistory, type RouteRecordRaw, type Router, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'
import SignUpPage from '../components/SignUpPage.vue'
import Login from '../components/Login.vue'
import ProjectDetails from '../components/project/ProjectDetails.vue'
import { useAuthStore, useOnboardingStore } from '@/stores'
import projectService from '@/services/project.service'

// Save the attempted URL for redirect after login
const saveAttemptedRoute = (to: RouteLocationNormalized): void => {
  if (to.path !== '/' && to.path !== '/callback') {
    sessionStorage.setItem('redirectPath', to.fullPath)
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../components/ResetPassword.vue'),
    meta: { title: 'Reset Password' }
  },
  {
    path: '/',
    name: 'Home',
    component: SignUpPage
  },
  {
    path: '/signup',
    name: 'SignUp',
    redirect: '/',
  },
  {
    path: '/payment/confirmation',
    name: 'PaymentConfirmation',
    component: () => import('../components/signup/PaymentConfirmation.vue'),
    meta: { requiresAuth: true, title: 'Payment Confirmation' }
  },
  {
    path: '/onboarding/company',
    name: 'CompanyOnboarding',
    component: () => import('../views/onboarding/CompanyOnboardingView.vue'),
    meta: { requiresAuth: true, title: 'Company setup' }
  },
  {
    path: '/onboarding/plan',
    name: 'PlanOnboarding',
    component: () => import('../views/onboarding/CompanyOnboardingView.vue'),
    meta: { requiresAuth: true, title: 'Choose a plan' }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('../views/admin/AdminDashboardView.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Admin Dashboard'
    }
  },
  {
    path: '/admin/contact-requests',
    name: 'AdminContactRequests',
    component: () => import('../components/admin/contact-requests/AdminContactRequests.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Contact Requests Administration'
    }
  },
  {
    path: '/admin/security',
    name: 'AdminSecurityDashboard',
    component: () => import('../views/admin/SecurityDashboardView.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Security Dashboard'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/sweethome3d/:projectId/:designId',
    name: 'sweethome3d',
    component: () => import('../views/SweetHome3DView.vue'),
    meta: { requiresAuth: true },
    props: route => ({
      projectId: route.params.projectId,
      designId: route.params.designId
    })
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('../components/AuthCallback.vue'),
  },
  {
    path: '/firms',
    name: 'Firms',
    component: () => import('../components/firm/FirmLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'FirmDashboard',
        component: () => import('../views/firm/FirmDashboardView.vue'),
        meta: { 
          requiresAuth: true,
          title: 'Dashboard'
        }
      },
      {
        path: 'client-dashboard',
        name: 'ClientDashboard',
        component: () => import('../views/client/ClientDashboardView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Client Dashboard'
        }
      },
      {
        path: 'users',
        name: 'FirmUsers',
        component: () => import('../views/firm/FirmUsersViewModern.vue'),
        meta: { 
          requiresAuth: true,
          title: 'Team Members'
        }
      },
      {
        path: 'intervenants',
        name: 'FirmIntervenants',
        component: () => import('../views/firm/FirmIntervenantsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Intervenants'
        }
      },
      {
        path: 'contacts',
        name: 'FirmContacts',
        component: () => import('../views/firm/FirmContactsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Contacts'
        }
      },
      {
        path: 'settings',
        name: 'FirmSettings',
        component: () => import('../views/firm/FirmSettingsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Firm Settings'
        }
      },
      {
        path: 'profile',
        name: 'FirmProfile',
        component: () => import('../views/firm/FirmProfileView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Firm Profile'
        }
      },
      {
        path: 'revenue',
        name: 'FirmRevenue',
        component: () => import('../views/firm/FirmRevenueView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Firm Revenue'
        }
      },
      {
        path: 'expenses',
        name: 'FirmExpenses',
        component: () => import('../views/firm/FirmExpensesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Firm Expenses'
        }
      },
      {
        path: 'project-templates',
        name: 'ProjectTemplates',
        component: () => import('../views/firm/ProjectTemplatesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Project Templates'
        }
      },
      {
        path: 'project-templates/create',
        name: 'CreateProjectTemplate',
        component: () => import('../views/firm/ProjectTemplateFormView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Create Project Template'
        }
      },
      {
        path: 'project-templates/edit/:id',
        name: 'EditProjectTemplate',
        component: () => import('../views/firm/ProjectTemplateFormView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Edit Project Template'
        }
      }
    ]
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../components/SettingsSection.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-subscription',
    name: 'MySubscription',
    component: () => import('../components/subscription/MySubscriptionSection.vue'),
    meta: { requiresAuth: true, title: 'My Subscription' }
  },
  {
    path: '/projects',
    name: 'Projects',
    component: () => import('../components/ProjectsSection.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: () => import('../views/CalendarView.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Calendar'
    }
  },
  {
    path: '/project/:projectId',
    name: 'ProjectDetails',
    component: ProjectDetails,
    meta: {
      requiresAuth: true,
      title: 'Project Details'
    },
    props: route => ({
      projectId: route.params.projectId,
      activeComponent: route.query.component,
      key: `${route.params.projectId}-${route.query.component || 'details'}`
    }),
    beforeEnter: async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      const projectId = to.params.projectId
      if (!projectId) {
        next('/projects')
        return
      }
      next()
    }
  },
  {
    path: '/project/:projectId/planning/printable',
    name: 'ProjectPlanningPrintable',
    component: () => import('../views/ProjectPlanningPrintableView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Project Planning - Printable'
    },
    props: route => ({
      projectId: route.params.projectId
    })
  },
  {
    path: '/project/:projectId/issues/:issueId',
    name: 'IssueDetails',
    component: () => import('../components/project/issues/IssueDetails.vue'),
    meta: {
      requiresAuth: true,
      title: 'Issue Details'
    }
  },
  {
    path: '/project/:projectId/notes/:noteId/discussion',
    name: 'NoteDiscussion',
    component: () => import('../components/project/notes/NoteDiscussionPage.vue'),
    meta: { requiresAuth: true, title: 'Note Discussion' },
    props: route => ({ projectId: route.params.projectId, noteId: route.params.noteId })
  },
  {
    path: '/project/:projectId/phases/:phaseId/configure',
    name: 'PhaseConfigurer',
    component: () => import('../views/PhaseConfigurerView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Configure Phase'
    },
    props: route => ({
      projectId: parseInt(route.params.projectId as string),
      phaseId: parseInt(route.params.phaseId as string)
    }),
    beforeEnter: async (to, from, next) => {
      try {
        const projectId = to.params.projectId as string
        const response = await projectService.getProjectById(projectId)
        
        // Redirect to project workspace if project is not multi-phase
        if (response?.data && !response.data.multiPhase) {
          next({ name: 'ProjectWorkspace', params: { projectId } })
        } else {
          next()
        }
      } catch (error) {
        console.error('Error checking project multi-phase status:', error)
        next() // Continue navigation on error
      }
    }
  },
  {
    path: '/project/:projectId/phases/:phaseId/workspace',
    name: 'PhaseWorkspace',
    component: () => import('../components/project/workspace/PhaseWorkspace.vue'),
    meta: {
      requiresAuth: true,
      title: 'Phase Workspace'
    },
    props: route => ({
      projectId: parseInt(route.params.projectId as string),
      phaseId: parseInt(route.params.phaseId as string)
    }),
    beforeEnter: async (to, from, next) => {
      try {
        const projectId = to.params.projectId as string
        const response = await projectService.getProjectById(projectId)
        
        // Redirect to project workspace if project is not multi-phase
        if (response?.data && !response.data.multiPhase) {
          next({ name: 'ProjectWorkspace', params: { projectId } })
        } else {
          next()
        }
      } catch (error) {
        console.error('Error checking project multi-phase status:', error)
        next() // Continue navigation on error
      }
    }
  },
  {
    path: '/admin/subscriptions',
    name: 'AdminSubscriptions',
    component: () => import('../components/admin/subscriptions/AdminSubscriptions.vue'),
    meta: { 
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Subscription Plans Administration'
    }
  },
  {
    path: '/admin/subscriptions/firms',
    name: 'AdminFirmSubscriptions',
    component: () => import('../components/admin/subscriptions/AdminFirmSubscriptions.vue'),
    meta: { 
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Firm Subscriptions Administration'
    }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('../components/admin/users/AdminUsers.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: 'Users Administration'
    }
  },
  // Tender routes
  {
    path: '/tenders',
    name: 'Tenders',
    component: () => import('../views/TendersView.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Tenders'
    }
  },
  {
    path: '/tenders/create',
    name: 'TenderCreate',
    component: () => import('../views/TenderCreateView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Create Tender'
    }
  },
  {
    path: '/tenders/:id',
    name: 'TenderDetails',
    component: () => import('../views/TenderDetailsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Tender Details'
    },
    props: route => ({ tenderId: route.params.id })
  },
  {
    path: '/tenders/update/:id',
    name: 'TenderUpdate',
    component: () => import('../views/TenderUpdateView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Update Tender'
    },
    props: route => ({ id: route.params.id })
  },
  {
    path: '/tenders/:id/apply',
    name: 'TenderApplication',
    component: () => import('../views/TenderApplicationView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Apply for Tender'
    },
    props: route => ({ tenderId: route.params.id })
  },
  {
    path: '/my-applications',
    name: 'MyApplications',
    component: () => import('../views/MyApplicationsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'My Applications'
    }
  },
  {
    path: '/my-applications/:id',
    name: 'ApplicationDetails',
    component: () => import('../views/ApplicationDetailsView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Application Details'
    },
    props: route => ({ applicationId: route.params.id })
  },
  {
    path: '/my-applications/:id/edit',
    name: 'ApplicationEdit',
    component: () => import('../views/ApplicationEditView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Edit Application'
    },
    props: route => ({ applicationId: route.params.id })
  },
  // Legal pages (public, no auth required)
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('../views/legal/LegalPolicyView.vue'),
    meta: { title: 'Privacy Policy' },
    props: { policyType: 'privacy' }
  },
  {
    path: '/cookie-policy',
    name: 'CookiePolicy',
    component: () => import('../views/legal/LegalPolicyView.vue'),
    meta: { title: 'Cookie Policy' },
    props: { policyType: 'cookie' }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: () => import('../views/legal/LegalPolicyView.vue'),
    meta: { title: 'Terms of Service' },
    props: { policyType: 'terms' }
  },
  {
    path: '/account-deletion',
    name: 'PublicAccountDeletion',
    component: () => import('../views/legal/PublicAccountDeletionView.vue'),
    meta: { title: 'Delete Account' }
  },
  // Catch-all route for 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../components/common/NotFound.vue'),
    meta: { title: 'Page Not Found' }
  }
]

const router: Router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Global navigation guard to check authentication
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  console.log('Router navigation:', {
    to: { path: to.path, name: to.name, params: to.params },
    from: { path: from.path, name: from.name }
  })

  // If the app hasn't finished initial auth check, wait for it so we don't redirect prematurely on page refresh
  const authStore = useAuthStore()
  const authChecked = authStore.authChecked === true
  
  if (!authChecked) {
    try {
      await authStore.checkAuth()
    } catch (err) {
      console.warn('Auth check failed during navigation guard:', err)
    }
  }

  const requiresAuth = to.matched.some(record => record.meta?.requiresAuth)
  const requiresAdmin = to.matched.some(record => record.meta?.requiresAdmin)
  const isAuthenticated = authStore.isAuthenticated
  const user = authStore.getUser

  if (requiresAuth && !isAuthenticated) {
    console.log('Authentication required, saving attempted route')
    saveAttemptedRoute(to)
    next('/')
  } else if (requiresAdmin && (!isAuthenticated || user?.type !== 'ADMIN')) {
    console.log('Admin privileges required (temporarily disabled)')
    // next('/client-dashboard') // Redirect to client dashboard if not admin - temporarily disabled
    next() // Allow navigation for testing
  } else if (isAuthenticated && (user?.type === 'FIRM_OWNER' || user?.type === 'FIRM_MEMBER')
      && !to.path.startsWith('/onboarding') && to.path !== '/payment/confirmation' && to.path !== '/callback') {
    const onboarding = useOnboardingStore()
    try {
      const status = await onboarding.refresh()
      if (user.type === 'FIRM_OWNER' && status.stage === 'COMPANY_REQUIRED') return next('/onboarding/company')
      if (status.stage === 'PLAN_REQUIRED') return next('/onboarding/plan')
    } catch (error) {
      console.warn('Unable to load onboarding status', error)
    }
    if (to.path === '/') next('/firms'); else next()
  } else if (to.path === '/' && isAuthenticated) {
    console.log('Authenticated user on home page, redirecting based on user type')
    const userType = user?.type
    if (userType === 'FIRM_OWNER' || userType === 'FIRM_MEMBER') {
      next('/firms')
    } else if (userType === 'CLIENT') {
      next('/firms/client-dashboard')
    } else if (userType === 'ADMIN') {
      // Admin users redirect to admin dashboard
      next('/admin')
    } else {
      // Fallback for any other user types
      next('/firms/client-dashboard')
    }
  } else if (to.path === '/callback') {
    // Don't save redirect for callback route
    next()
  } else {
    // Update page title if meta title exists
    if (to.meta?.title) {
      document.title = `${to.meta.title} - Bati Vision 360`
    }
    next()
  }
})

// Add afterEach hook to debug successful navigation
router.afterEach((to: RouteLocationNormalized) => {
  console.log('Navigation completed to:', to.path)
})

export default router
